import type WaTreeItem from '@awesome.me/webawesome-pro/dist/components/tree-item/tree-item.js'
import { find, forEach, map, pipe } from 'remeda'
import { readRenderedLiveProp } from './live-props'

// Private functions -----------------------------------------------------------

// Finds trees still hidden during their first client hydration.
const hydratingTrees = (root: ParentNode): readonly HTMLElement[] =>
  root instanceof HTMLElement && root.matches('wa-tree[data-hydrating]')
    ? [root]
    : Array.from(root.querySelectorAll<HTMLElement>('wa-tree[data-hydrating]'))

// Lists tree items managed by the live props contract.
const treeItems = (root: ParentNode): readonly WaTreeItem[] =>
  Array.from(root.querySelectorAll<WaTreeItem>('wa-tree-item[data-live-props]'))

// Finds the tree item selected by the latest server contract.
const selectedTreeItem = (root: ParentNode): WaTreeItem | null =>
  pipe(
    treeItems(root),
    find(
      (item) => readRenderedLiveProp(item, 'interactive', 'selected') === true
    )
  ) ?? null

// Waits for item updates before revealing hydrated trees.
const finishHydration = async (
  root: ParentNode,
  items: readonly WaTreeItem[],
  signal?: AbortSignal
): Promise<void> => {
  await Promise.all(
    pipe(
      items,
      map((item) => item.updateComplete)
    )
  )

  if (signal?.aborted) {
    return
  }

  pipe(
    hydratingTrees(root),
    forEach((tree) => tree.removeAttribute('data-hydrating'))
  )
}

// Public functions ------------------------------------------------------------

// Applies rendered expansion before the tree's first visible paint.
export const syncTreeExpansion = async (
  root: ParentNode,
  signal?: AbortSignal
): Promise<void> => {
  const items = treeItems(root)

  pipe(
    items,
    forEach((item) => {
      const expanded =
        readRenderedLiveProp(item, 'interactive', 'expanded') === true

      if (item.expanded !== expanded) {
        // Nested items connect before their parent's context reflects the
        // server attribute, so apply that input before the first paint.
        item.expanded = expanded
      }
    })
  )

  await finishHydration(root, items, signal)
}

// Completes hydration after a LiveView patch.
export const finishTreeHydration = (
  root: ParentNode,
  signal?: AbortSignal
): Promise<void> => finishHydration(root, treeItems(root), signal)

// Reads the selected item's stable DOM id.
export const selectedTreeItemId = (root: ParentNode): string | null =>
  selectedTreeItem(root)?.id || null

// Scrolls a changed selection into view inside a Facade scroller.
export const revealSelectedTreeItem = (root: HTMLElement): void => {
  if (!root.closest('wa-scroller[data-component=".scroller"]')) {
    return
  }

  selectedTreeItem(root)?.scrollIntoView({
    block: 'nearest',
    inline: 'nearest'
  })
}
